import { css } from 'aphrodite/no-important';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { isExhibitorUser } from 'redux/reducers/user';
import { RootState } from 'redux/store';
import { ROUTE } from 'util/constants';
import { GAEventAction, GAEventCategory, GAEventLabel, trackGoogleEvent } from 'util/google-helpers';
import { style } from './style';

export interface IQuickLink {
  url: string;
  text: string;
  icon: string;
  home_visibility: string;
}

interface Props {
  cmsData: any;
}

export const QuickLinks: FC<Props> = ({ cmsData }) => {
  const links = cmsData;
  const NOT_HOVERED = -1;
  const userIsLoggedIn = useSelector((state: RootState) => !!state.user.data);
  const isExhibitor = useSelector(isExhibitorUser);
  const history = useHistory();

  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [linkTouches, setLinkTouches] = useState<Array<number>>(new Array(links.length).fill(0));
  const [hovered, setHovered] = useState(NOT_HOVERED);
  const linkRefs = useRef<Array<HTMLAnchorElement | null>>([]);
  const styles = style({});

  useEffect(() => {
    function handleOutsideTouch(event: any) {
      const outside = !linkRefs.current.some((ref) => ref?.contains(event.target));
      if (outside) {
        setLinkTouches(new Array(links.length).fill(0));
        setHovered(NOT_HOVERED);
      }
    }
    document.addEventListener('touchend', handleOutsideTouch);
    return () => document.removeEventListener('touchend', handleOutsideTouch);
  }, [NOT_HOVERED, links.length]);

  const onTouch = (index: number) => {
    const currentTouches: number = linkTouches[index];
    const updatedTouches: Array<number> = new Array(links.length).fill(0);
    updatedTouches[index] = currentTouches + 1;
    setLinkTouches(updatedTouches);
  };

  const handleLinkClick = (link: IQuickLink) => {
    trackGoogleEvent(GAEventCategory.SITE, GAEventAction.CLICK, `${GAEventLabel.QUICK_LINK} | ${link.text}`);
    if (link.url.trim().charAt(0) === '/') history.push(link.url);
  };

  const onClick = (event: any, index: number, link: IQuickLink) => {
    if (isTouchDevice) {
      if (linkTouches[index] < 2) {
        event.preventDefault();
        setHovered(index);
        return;
      } else {
        setLinkTouches(new Array(links.length).fill(0));
        setHovered(NOT_HOVERED);
      }
    }
    handleLinkClick(link);
  };

  const getQuickLinksList = (): JSX.Element[] | null => {
    return links
      .filter((link: IQuickLink) => {
        return (isExhibitor && link.url === ROUTE.SURVEY) || link.url.trim() === '' ? false : true;
      })
      .map((link: IQuickLink, index: number) => {
        const customStyle = style({
          hide: !userIsLoggedIn && link.home_visibility === 'false',
          hovered: hovered === index,
        });
        if (link.url.trim().charAt(0) === '/') {
          return (
            // eslint-disable-next-line
            <a
              ref={(node) => (linkRefs.current[index] = node)}
              key={index}
              onMouseEnter={() => setHovered(index)}
              onMouseLeave={() => setHovered(NOT_HOVERED)}
              onTouchStart={() => !isTouchDevice && setIsTouchDevice(true)}
              onTouchEnd={() => onTouch(index)}
              onClick={(event) => onClick(event, index, link)}
              className={css(customStyle.link)}>
              <img src={link.icon} alt=''></img>
              <span>{link.text}</span>
            </a>
          );
        } else {
          return (
            <a
              ref={(node) => (linkRefs.current[index] = node)}
              key={index}
              href={link.url}
              target='_blank'
              rel='noopener noreferrer'
              onMouseEnter={() => setHovered(index)}
              onMouseLeave={() => setHovered(NOT_HOVERED)}
              onTouchStart={() => {
                !isTouchDevice && setIsTouchDevice(true);
              }}
              onTouchEnd={() => {
                onTouch(index);
              }}
              onClick={(event) => onClick(event, index, link)}
              className={css(customStyle.link)}>
              <img src={link.icon} alt=''></img>
              <span>{link.text}</span>
            </a>
          );
        }
      });
  };

  return <div className={css(styles.container)}>{getQuickLinksList()}</div>;
};
